import * as THREE from "three";

import type { PlanePool } from "./PlanePool";
import { findSiblingVideo, OriginalMedia } from "./WorkListing";

interface Card {
    root: HTMLElement;
    el: HTMLElement;
    video: HTMLVideoElement | null;
}

const REVEAL_INIT_DELAY_MS = 4000;
const REVEAL_STAGGER_MS = 800;
const REVERT_STAGGER_MS = 300;
const JITTER_MS = 150;
const HOVER_INTENT_MS = 250;

const hoverActiveClass = "is-hover-active";
const mirrorActiveClass = "is-mirror-active";

export function initFilmWorkListing(planePool: PlanePool, root: HTMLElement): () => void {
    const cardRoots = root.querySelectorAll<HTMLElement>(".work-card");
    if (!cardRoots.length) return () => {};

    const cards: Card[] = Array.from(cardRoots)
        .map(cardRoot => {
            const el = cardRoot.querySelector<HTMLElement>("[data-webgl]");
            if (!el) return null;
            const video = findSiblingVideo(el);

            if (video && video.crossOrigin !== "anonymous") {
                video.crossOrigin = "anonymous";
            }

            return { root: cardRoot, el, video };
        })
        .filter((c): c is Card => c !== null);

    if (!cards.length) return () => {};

    const originals = new WeakMap<THREE.ShaderMaterial, OriginalMedia>();
    const videoTextures = new Map<HTMLVideoElement, THREE.VideoTexture>();
    const visible = new Set<Card>();
    const pendingTimeouts = new Map<Card, number>();
    const pauseTimeouts = new Map<Card, number>();
    const revertTimeouts = new Map<Card, number>();
    const revealedSiblings = new Set<Card>();
    let hoverIntentId: number | undefined;

    const cleanups: (() => void)[] = [];
    let activeCard: Card | null = null;
    let navigating = false;

    const io = new IntersectionObserver(
        entries => {
            entries.forEach(entry => {
                const card = cards.find(c => c.root === entry.target);
                if (!card) return;

                if (entry.isIntersecting) {
                    visible.add(card);
                } else {
                    visible.delete(card);
                    cancelPending(card);
                    if (card !== activeCard && !navigating) {
                        revertCard(card);
                        clearState(card);
                    }
                }
            });
        },
        { threshold: 0.1, rootMargin: `${window.innerHeight}px 0px` }
    );
    cards.forEach(c => io.observe(c.root));

    function getVideoTexture(video: HTMLVideoElement): THREE.VideoTexture {
        let tex = videoTextures.get(video);
        if (!tex) {
            tex = new THREE.VideoTexture(video);
            tex.minFilter = THREE.LinearFilter;
            tex.magFilter = THREE.LinearFilter;
            videoTextures.set(video, tex);
        }
        return tex;
    }

    function cancelRevert(card: Card) {
        const id = revertTimeouts.get(card);
        if (id !== undefined) {
            window.clearTimeout(id);
            revertTimeouts.delete(card);
        }
    }

    function applyVideoTexture(card: Card, video: HTMLVideoElement, texture: THREE.VideoTexture) {
        const plane = planePool.getPlane(card.el);
        if (!plane) return;
        const mat = plane.mesh.material as THREE.ShaderMaterial;

        if (!originals.has(mat)) {
            originals.set(mat, {
                texture: mat.uniforms.uTexture.value,
                aspect: mat.uniforms.uImageAspect.value,
            });
        }

        mat.uniforms.uTexture.value = texture;
        mat.uniforms.uImageAspect.value = video.videoWidth / video.videoHeight || mat.uniforms.uImageAspect.value;

        if (!video.videoWidth) {
            video.addEventListener(
                "loadedmetadata",
                () => {
                    if (mat.uniforms.uTexture.value === texture) {
                        mat.uniforms.uImageAspect.value = video.videoWidth / video.videoHeight;
                    }
                },
                { once: true }
            );
        }
    }

    function applyTexture(card: Card, texture: THREE.Texture, aspect: number) {
        cancelRevert(card); // fresh reveal > scheduled revert

        const plane = planePool.getPlane(card.el);
        if (!plane) return;
        const mat = plane.mesh.material as THREE.ShaderMaterial;

        if (!originals.has(mat)) {
            originals.set(mat, {
                texture: mat.uniforms.uTexture.value,
                aspect: mat.uniforms.uImageAspect.value,
            });
        }

        mat.uniforms.uTexture.value = texture;
        mat.uniforms.uImageAspect.value = aspect;
    }

    function revertCard(card: Card) {
        cancelRevert(card);

        const plane = planePool.getPlane(card.el);
        if (!plane) return;
        const mat = plane.mesh.material as THREE.ShaderMaterial;
        const original = originals.get(mat);
        if (original) {
            mat.uniforms.uTexture.value = original.texture;
            mat.uniforms.uImageAspect.value = original.aspect;
        }
        revealedSiblings.delete(card);
    }

    function cancelPending(card: Card) {
        const id = pendingTimeouts.get(card);
        if (id !== undefined) {
            window.clearTimeout(id);
            pendingTimeouts.delete(card);
        }
    }

    function clearState(card: Card) {
        card.root.classList.remove(hoverActiveClass, mirrorActiveClass);
    }

    function setHoverState(card: Card) {
        card.root.classList.add(hoverActiveClass);
        card.root.classList.remove(mirrorActiveClass);
    }

    function setMirrorState(card: Card) {
        card.root.classList.add(mirrorActiveClass);
        card.root.classList.remove(hoverActiveClass);
    }

    cards.forEach(card => {
        if (!card.video) return;

        const startHover = () => {
            activeCard = card;

            setHoverState(card);

            const video = card.video!;
            const pauseId = pauseTimeouts.get(card);
            if (pauseId !== undefined) {
                window.clearTimeout(pauseId);
                pauseTimeouts.delete(card);
            }
            void video.play().catch(() => {});

            const tex = getVideoTexture(video);
            const aspect = video.videoWidth / video.videoHeight || 1;

            const swapIn = () => {
                if (activeCard !== card) return;
                applyVideoTexture(card, video, tex);
            };

            if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
                swapIn();
            } else {
                video.addEventListener("loadeddata", swapIn, { once: true });
            }

            if (!video.videoWidth) {
                video.addEventListener(
                    "loadedmetadata",
                    () => {
                        const correctAspect = video.videoWidth / video.videoHeight;

                        const plane = planePool.getPlane(card.el);
                        const mat = plane?.mesh.material as THREE.ShaderMaterial | undefined;
                        if (mat && mat.uniforms.uTexture.value === tex) {
                            mat.uniforms.uImageAspect.value = correctAspect;
                        }

                        revealedSiblings.forEach(sibling => {
                            const siblingPlane = planePool.getPlane(sibling.el);
                            const siblingMat = siblingPlane?.mesh.material as THREE.ShaderMaterial | undefined;
                            if (siblingMat && siblingMat.uniforms.uTexture.value === tex) {
                                siblingMat.uniforms.uImageAspect.value = correctAspect;
                            }
                        });
                    },
                    { once: true }
                );
            }

            const siblings = shuffle([...visible].filter(c => c !== card));
            siblings.forEach((sibling, i) => {
                const spread = siblings.length > 1 ? i / (siblings.length - 1) : 0;
                const delay = REVEAL_INIT_DELAY_MS + spread * REVEAL_STAGGER_MS + Math.random() * JITTER_MS;

                const id = window.setTimeout(() => {
                    pendingTimeouts.delete(sibling);
                    if (activeCard !== card) return;
                    const currentAspect = video.videoWidth / video.videoHeight || aspect;
                    applyTexture(sibling, tex, currentAspect);
                    setMirrorState(sibling);
                    revealedSiblings.add(sibling);
                }, delay);

                pendingTimeouts.set(sibling, id);
            });
        };

        const onEnter = () => {
            if (hoverIntentId !== undefined) window.clearTimeout(hoverIntentId);
            hoverIntentId = window.setTimeout(() => {
                hoverIntentId = undefined;
                startHover();
            }, HOVER_INTENT_MS);
        };

        const onLeave = () => {
            if (hoverIntentId !== undefined) {
                window.clearTimeout(hoverIntentId);
                hoverIntentId = undefined;
            }
            if (navigating) return;
            if (activeCard !== card) return;

            activeCard = null;

            const video = card.video!;

            // cancel reveals that hadn't fired yet
            pendingTimeouts.forEach(id => window.clearTimeout(id));
            pendingTimeouts.clear();

            // own card reverts immediately
            revertCard(card);

            // siblings revert in stagger
            const toRevert = shuffle([...revealedSiblings]);
            revealedSiblings.clear();

            clearState(card);

            let maxDelay = 0;
            toRevert.forEach((sibling, i) => {
                const spread = toRevert.length > 1 ? i / (toRevert.length - 1) : 0;
                const delay = spread * REVERT_STAGGER_MS + Math.random() * JITTER_MS;
                maxDelay = Math.max(maxDelay, delay);

                const id = window.setTimeout(() => {
                    revertTimeouts.delete(sibling);
                    revertCard(sibling);
                    clearState(sibling);
                }, delay);
                revertTimeouts.set(sibling, id);
            });

            // Don't stop the video until every sibling has actually swapped back
            const pauseId = window.setTimeout(() => {
                pauseTimeouts.delete(card);
                video.pause();
                video.currentTime = 0;
            }, maxDelay + 50);
            pauseTimeouts.set(card, pauseId);
        };

        card.root.addEventListener("mouseenter", onEnter);
        card.root.addEventListener("mouseleave", onLeave);

        cleanups.push(() => {
            card.root.removeEventListener("mouseenter", onEnter);
            card.root.removeEventListener("mouseleave", onLeave);
        });
    });

    function resetAll() {
        pendingTimeouts.forEach(id => window.clearTimeout(id));
        pendingTimeouts.clear();
        revertTimeouts.forEach(id => window.clearTimeout(id));
        revertTimeouts.clear();
        pauseTimeouts.forEach(id => window.clearTimeout(id));
        pauseTimeouts.clear();

        activeCard = null;
        revealedSiblings.clear();

        cards.forEach(card => {
            revertCard(card);
            clearState(card);

            const video = card.video;
            if (!video) return;
            video.pause();
            video.currentTime = 0;
        });
    }

    const onNavStart = () => (navigating = true);
    const onNavEnd = () => {
        navigating = false;
        resetAll();
    };

    window.addEventListener("barba:nav-start", onNavStart);
    window.addEventListener("barba:nav-end", onNavEnd);

    return function destroy() {
        window.removeEventListener("barba:nav-start", onNavStart);
        window.removeEventListener("barba:nav-end", onNavEnd);
        io.disconnect();
        cleanups.forEach(fn => fn());
        if (hoverIntentId !== undefined) window.clearTimeout(hoverIntentId);
        resetAll();
        videoTextures.forEach(tex => tex.dispose());
        videoTextures.clear();
    };
}

function shuffle<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}
